// TODO: Add routing hook that takes care of duality (modal vs. browser-routing) and use it here

import { ReactElement } from 'react';
import { HStack, VStack, Text, Box, Image } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { ChevronRightIcon, FileIcon, FolderIcon } from 'assets/icons/v2';
import { CtfImage } from '@novaheal/types';
import { useFirebase } from 'hooks/useFirebase';
import {
  useIsInModal,
  useModalNavigation,
} from 'components/common/Modal/variants/worksheet/components/ModalNavigationContext';

type Props = {
  id: string;
  image: CtfImage;
  title: string;
  numberOfArticles?: number;
  numberOfFolders?: number;
};

const IMAGE_WIDTH = '65px';

export const CategoryCard = ({
  id,
  image,
  title,
  numberOfArticles,
  numberOfFolders,
}: Props): ReactElement | null => {
  const { logFirebaseClick } = useFirebase();
  const shouldShowCategory = !!numberOfArticles || !!numberOfFolders;
  const navigate = useNavigate();
  const modalNavigation = useModalNavigation();
  const isInModal = useIsInModal();

  if (!shouldShowCategory) return null;

  const shouldShowNumberOfArticles = !!numberOfArticles && numberOfArticles > 0;
  const shouldShowNumberOfFolders = !!numberOfFolders && numberOfFolders > 0;

  const handleClick = (): void => {
    logFirebaseClick('category-card', title);
    if (isInModal && modalNavigation) {
      modalNavigation.navigate({
        name: 'category',
        params: { id },
      });
      return;
    }
    navigate(`${ProtectedRoutes.CATEGORY}/${id}`);
  };

  return (
    <Box
      cursor="pointer"
      onClick={handleClick}
      _hover={{ bg: 'background.canvas' }}
      _active={{ bg: 'background.canvas' }}
      px="10px"
    >
      <HStack position="relative" py="m" gap="15px">
        <Box p="7.5px">
          <Image
            src={image.url}
            alt={image.description}
            width="50px"
            height="50px"
            borderRadius="full"
            overflow="hidden"
            border="0.2px solid rgba(0, 0, 0, 0.26)"
            objectFit="cover"
          />
        </Box>

        <VStack align="left" flex={1} gap="15px">
          <Text as="h3" textStyle="h4.medium" color="text.secondary">
            {title}
          </Text>
          <HStack color="text.tertiary" gap="15px" alignItems="center">
            {shouldShowNumberOfArticles && (
              <HStack gap="5px" alignItems="center">
                <FileIcon boxSize="18px" color="icon.quaternary" />
                <Text color="text.tertiary" textStyle="body.s.regular">
                  {numberOfArticles} Lernkarten
                </Text>
              </HStack>
            )}
            {shouldShowNumberOfFolders && (
              <HStack gap="5px" alignItems="center">
                <FolderIcon boxSize="18px" color="icon.quaternary" />
                <Text color="text.tertiary" textStyle="body.s.regular">
                  {numberOfFolders} Ordner
                </Text>
              </HStack>
            )}
          </HStack>
        </VStack>
        <ChevronRightIcon boxSize="18px" color="text.quaternary" />
      </HStack>
      <Box
        h="1px"
        w={`calc(100% - ${IMAGE_WIDTH} - 15px)`}
        borderColor="border.accent"
        bottom="1px"
        ml="auto"
      />
    </Box>
  );
};
